import { encodeAllURLParameters } from "@/common/api.utilities";
import { propertyByLanguage, splitBySentence } from "@/common/basic.utilities";
import { ENDPOINT_BREAKING_EVENTS } from "@/common/constants/api";
import { httpGet } from "@/services/api.service";
import { getUrlDomain } from "@/common/basic.utilities";
import { getCountryLookup, getEventCountries } from "@/common/maps/utilities";

export { _getBreakingEvents as getBreakingEvents };

/**
 * Gets Breaking Events
 * Expected params:{ bounds, start, end, pageSize, pageOffset, sortDirection, status: BreakingEventsStatus, search: string match, aoiId }
 * @param params
 * @param requestTimestamp
 * @returns {Promise<any|null>}
 */
const _getBreakingEvents = async (params, requestTimestamp) => {
  try {
    const url = encodeAllURLParameters({
      baseEndpoint: ENDPOINT_BREAKING_EVENTS,
      params: _formatParams(params),
    });
    const result = await httpGet({ url });
    const { data } = JSON.parse(result);

    await _formatFeatures(data.features, requestTimestamp);

    return data;
  } catch (error) {
    console.error(
      "Issue getting breaking events in breakingEvents.service.js:",
      error
    );
    return null;
  }
};

/**
 * Format/Validate/Cleanup params
 * Expected params:{ bounds, start, end, pageSize, pageOffset, sortDirection, status: BreakingEventsStatus, search: string match, aoiId }
 * @param params
 * @returns {Object}
 */
const _formatParams = (params) => {
  if (!params.start) {
    throw new Error("Breaking events API: missing or invalid start");
  }
  return {
    start: params.start,
    end: params.end ?? null,
    pageSize: params.pageSize ?? 25,
    pageOffset: params.pageOffset ?? 0,
    sortDirection: params.sortDirection ?? "desc",
    aoiId: params.aoiId ?? null,
    search: params.search ?? null,
    status: params.status ?? null,
    bounds: params.bounds ?? null,
  };
};

const _formatFeatures = async (features, requestTimestamp) => {
  const countryLookup = await getCountryLookup();
  for (const feature of features) {
    await _formatFeature(feature, requestTimestamp, countryLookup);
  }
};

/**
 *
 * @param feature
 * @param requestTimestamp
 * @param countryLookup
 * @returns {Promise<void>}
 */
const _formatFeature = async (feature, requestTimestamp, countryLookup) => {
  if (feature.properties) {
    feature.properties.formatted = {};
    feature.properties.formatted.language = feature.properties.language ?? "en";
    feature.properties.formatted.requestTimestamp = requestTimestamp
      ? requestTimestamp
      : new Date().toISOString();
    feature.properties.formatted.esId = feature.properties.id ?? null;
    feature.properties.formatted.title =
      `${feature.properties.status}: ${propertyByLanguage(
        feature.properties.title,
        feature.properties.formatted.language
      )}` ?? null;
    feature.properties.formatted.body = propertyByLanguage(
      feature.properties.content,
      feature.properties.formatted.language
    );
    const summaryBySentence = splitBySentence(
      propertyByLanguage(
        feature.properties.content,
        feature.properties.formatted.language
      )
    );

    feature.properties.formatted.summary = "";
    const numOfSentencesDisplayed = summaryBySentence.length === 1 ? 1 : 2; // This can be adjusted by product team but allows it to be dynamic
    for (let s = 0; s < numOfSentencesDisplayed; s++) {
      feature.properties.formatted.summary += summaryBySentence[s];
    }

    feature.properties.formatted.countriesDisplayMetadata = getEventCountries(
      [feature.properties.location?.metadata?.countryCodes],
      countryLookup
    );

    feature.properties.formatted.location =
      feature.properties.location?.point ?? null;
    feature.properties.formatted.source = feature.properties.source
      ? getUrlDomain(feature.properties.source) ?? null
      : null;
    feature.properties.formatted.link = feature.properties.source ?? null;
    feature.properties.formatted.occurred =
      feature.properties["@timestamp"] ?? null;

    feature.properties.formatted.type = feature.properties.subtype;

    feature.properties.formatted.initialPublishedDate =
      feature.properties.initialPublishedDate || null;
    feature.properties.formatted.revisedPublishedDate =
      feature.properties.revisedPublishedDate || null;
  }
};
